import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  UserCalculateFiatDepositCostInput,
  UserCalculateWithdrawalCostInput,
  UserCancelFiatWithdrawalInput,
  UserGetFiatTransactionHistoryInput,
  UserPaymentInitiateFiatDepositInput,
  UserPaymentVerifyFiatDepositInput,
  UserFiatWithdrawInput,
  UserInitiateCryptoDepositInput,
  UserGetAvailableCryptoContractsParams,
} from '../api'
import type { IMutationArgs } from '../types'
import {
  calculateDepositCostOptions,
  calculateWithdrawalCostOptions,
  getFiatTransactionHistoryOptions,
  paymentVerifyDepositFiatOptions,
  userGetAvailableCryptoContractsOptions,
} from './user-payment.options'
import userPaymentService from './user-payment.service'
import { EUserPaymentQueryKey } from './user-payment.types'
import { EUserPortfolioQueryKey } from '../user-portfolio'

export const usePaymentInitiateDepositFiat = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args

  return useMutation({
    mutationFn: (data: UserPaymentInitiateFiatDepositInput) =>
      userPaymentService.paymentInitiateDepositFiat(data),
    mutationKey: [EUserPaymentQueryKey.PAYMENT_INITIATE_DEPOSIT_FIAT],
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EUserPaymentQueryKey.PAYMENT_VERIFY_DEPOSIT_FIAT],
        })
      }
      queryClient.invalidateQueries({
        queryKey: [EUserPortfolioQueryKey.USER_PORTFOLIO],
      })

      return data
    },
  })
}

export const useInitiateCryptoDepositInput = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: UserInitiateCryptoDepositInput) =>
      userPaymentService.initiateCryptoDepositInput(data),
    mutationKey: [EUserPaymentQueryKey.INITIATE_CRYPTO_DEPOSIT_INPUT],
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EUserPaymentQueryKey.GET_AVAILABLE_CRYPTO_CONTRACTS],
        })
      }
      queryClient.invalidateQueries({
        queryKey: [EUserPortfolioQueryKey.USER_PORTFOLIO],
      })
      return data
    },
  })
}

export const useUserGetAvailableCryptoContracts = (
  args: UserGetAvailableCryptoContractsParams,
) => {
  return useQuery(userGetAvailableCryptoContractsOptions(args))
}
export const useInitiateWithdrawFiat = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: UserFiatWithdrawInput) =>
      userPaymentService.initiateWithdrawalFiat(data),
    mutationKey: [EUserPaymentQueryKey.INITIATE_WITHDRAW_FIAT],
    onSuccess: (data) => {
      if (invalidate)
        queryClient.invalidateQueries({
          queryKey: [EUserPaymentQueryKey.GET_FIAT_TRANSACTION_HISTORY],
        })
      queryClient.invalidateQueries({
        queryKey: [EUserPortfolioQueryKey.USER_PORTFOLIO],
      })
      return data
    },
  })
}

export const usePaymentVerifyDepositFiat = (
  args: UserPaymentVerifyFiatDepositInput,
) => {
  return useQuery(paymentVerifyDepositFiatOptions(args))
}

export const useGetFiatTransactionHistory = (
  args: UserGetFiatTransactionHistoryInput,
) => {
  return useInfiniteQuery(getFiatTransactionHistoryOptions(args))
}

export const useCalculateDepositCost = (
  args: UserCalculateFiatDepositCostInput,
) => {
  return useQuery(calculateDepositCostOptions(args))
}

export const useCalculateWithdrawalCost = (
  args: UserCalculateWithdrawalCostInput,
) => {
  return useQuery(calculateWithdrawalCostOptions(args))
}

export const useCancelWithdrawalFiat = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: UserCancelFiatWithdrawalInput) =>
      userPaymentService.cancelWithdrawalFiat(data),
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EUserPaymentQueryKey.GET_FIAT_TRANSACTION_HISTORY],
        })
      }
      queryClient.invalidateQueries({
        queryKey: [EUserPortfolioQueryKey.USER_PORTFOLIO],
      })
      return data
    },
  })
}
